import React, { useState, useEffect } from 'react';
import { plansAPI, uploadAPI, buildingTypesAPI } from '../../lib/api';
import { X, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import FileUploader from './FileUploader';

const DRAWING_SETS_META = [
  { key: 'architectural', label: 'Architectural Drawings', required: true },
  { key: 'structural', label: 'Structural Drawings' },
  { key: 'mechanical', label: 'Mechanical Drawings' },
  { key: 'electrical', label: 'Electrical Drawings' },
  { key: 'bill_of_quantities', label: 'Bill of Quantities' },
  { key: 'local_council', label: 'Local Council Submission' }
];

const emptyDrawingSet = { enabled: false, price: 0, pdf_file_id: null, cad_file_id: null, pdf_filename: null, cad_filename: null };

const emptyPlan = () => ({
  reference_number: '',
  name: '',
  description: '',
  area_sqm: 100,
  min_stand_size_sqm: 300,
  bedrooms: 3,
  bathrooms: 2,
  floors: 1,
  height_m: 3.5,
  estimated_construction_cost: 0,
  plan_type: 'A',
  base_price: 2500,
  image_url: '',
  images: [],
  image_items: [],
  style: '',
  specifications: {},
  drawing_sets: {
    architectural: { enabled: true, price: 2500, pdf_file_id: null, cad_file_id: null },
    structural: { ...emptyDrawingSet },
    mechanical: { ...emptyDrawingSet },
    electrical: { ...emptyDrawingSet },
    bill_of_quantities: { ...emptyDrawingSet },
    local_council: { ...emptyDrawingSet }
  }
});

const PlanForm = ({ existingPlan, onSaved, onCancel }) => {
  const [formData, setFormData] = useState(existingPlan ? { ...emptyPlan(), ...existingPlan } : emptyPlan());
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [buildingTypes, setBuildingTypes] = useState([]);

  useEffect(() => {
    buildingTypesAPI.getBuildingTypes().then(r => setBuildingTypes(r.data)).catch(() => {});
  }, []);

  // Ensure drawing_sets has all keys
  useEffect(() => {
    if (existingPlan) {
      const ds = { ...emptyPlan().drawing_sets };
      Object.keys(existingPlan.drawing_sets || {}).forEach(k => {
        ds[k] = { ...ds[k], ...existingPlan.drawing_sets[k] };
      });
      ds.architectural.enabled = true;
      // Ensure image_items align with images list
      const imgs = existingPlan.images || (existingPlan.image_url ? [existingPlan.image_url] : []);
      const existingItems = existingPlan.image_items || [];
      const itemMap = new Map(existingItems.map(it => [it.url, it.name]));
      const image_items = imgs.map(url => ({ url, name: itemMap.get(url) || '' }));
      setFormData({ ...emptyPlan(), ...existingPlan, drawing_sets: ds, images: imgs, image_items });
    }
  }, [existingPlan]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const response = await uploadAPI.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${response.data.file_id}`;
      setFormData(prev => {
        const isFirst = !prev.image_url;
        const newImages = [...prev.images, imageUrl];
        const newItems = [...(prev.image_items || []), { url: imageUrl, name: '' }];
        return {
          ...prev,
          image_url: isFirst ? imageUrl : prev.image_url,
          images: newImages,
          image_items: newItems
        };
      });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== idx);
      const newItems = (prev.image_items || []).filter((_, i) => i !== idx);
      return {
        ...prev,
        images: newImages,
        image_items: newItems,
        image_url: newImages[0] || ''
      };
    });
  };

  const setImageName = (idx, name) => {
    setFormData(prev => {
      const items = [...(prev.image_items || [])];
      if (items[idx]) items[idx] = { ...items[idx], name };
      return { ...prev, image_items: items };
    });
  };

  const toggleDrawingSet = (key) => {
    if (key === 'architectural') return;
    setFormData(prev => ({
      ...prev,
      drawing_sets: {
        ...prev.drawing_sets,
        [key]: { ...prev.drawing_sets[key], enabled: !prev.drawing_sets[key].enabled }
      }
    }));
  };

  const setDrawingSetPrice = (key, price) => {
    setFormData(prev => ({
      ...prev,
      drawing_sets: {
        ...prev.drawing_sets,
        [key]: { ...prev.drawing_sets[key], price }
      }
    }));
  };

  const setDrawingSetFile = (key, fileType, fileData) => {
    // fileType: 'pdf' or 'cad'
    setFormData(prev => ({
      ...prev,
      drawing_sets: {
        ...prev.drawing_sets,
        [key]: {
          ...prev.drawing_sets[key],
          [`${fileType}_file_id`]: fileData?.file_id || null,
          [`${fileType}_filename`]: fileData?.filename || null
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }
    if (!formData.reference_number?.trim()) {
      toast.error('Reference number is required (used in the plan URL)');
      return;
    }
    if (!formData.image_url) {
      toast.error('Please upload at least one plan image');
      return;
    }

    setSaving(true);
    try {
      // Clean up drawing_sets - remove filename helpers before sending
      const cleanedDrawingSets = {};
      Object.keys(formData.drawing_sets).forEach(k => {
        cleanedDrawingSets[k] = {
          enabled: formData.drawing_sets[k].enabled,
          price: parseFloat(formData.drawing_sets[k].price || 0),
          pdf_file_id: formData.drawing_sets[k].pdf_file_id || null,
          cad_file_id: formData.drawing_sets[k].cad_file_id || null
        };
      });

      // base_price mirrors the architectural drawing set price for listing display
      const basePrice = parseFloat(cleanedDrawingSets.architectural?.price || formData.base_price || 0);

      const payload = {
        ...formData,
        drawing_sets: cleanedDrawingSets,
        reference_number: formData.reference_number?.trim() || '',
        area_sqm: parseInt(formData.area_sqm),
        min_stand_size_sqm: parseInt(formData.min_stand_size_sqm || 0),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms || 1),
        floors: parseInt(formData.floors),
        height_m: parseFloat(formData.height_m || 0),
        estimated_construction_cost: parseFloat(formData.estimated_construction_cost || 0),
        base_price: basePrice,
        image_items: (formData.image_items || []).map(it => ({ url: it.url, name: it.name || '' }))
      };

      if (existingPlan) {
        await plansAPI.updatePlan(existingPlan.plan_id, payload);
        toast.success('Plan updated');
      } else {
        await plansAPI.createPlan(payload);
        toast.success('Plan created');
      }
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="plan-form-modal">
      <div className="bg-white border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {existingPlan ? 'Edit Plan' : 'Add New Plan'}
          </h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded" data-testid="close-form-btn">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <section>
            <h3 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Basic Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  required
                  data-testid="plan-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reference Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. UDS-2026-001"
                  value={formData.reference_number || ''}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  required
                  data-testid="plan-reference-input"
                />
                <p className="text-xs text-text_muted mt-1">Used as the plan's URL (e.g. /plans/UDS-2026-001)</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  required
                  data-testid="plan-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Total Area (m²)</label>
                <input
                  type="number"
                  value={formData.area_sqm}
                  onChange={(e) => setFormData({ ...formData, area_sqm: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-area-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Stand Size (m²)</label>
                <input
                  type="number"
                  value={formData.min_stand_size_sqm}
                  onChange={(e) => setFormData({ ...formData, min_stand_size_sqm: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-stand-size-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bedrooms</label>
                <select
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-bedrooms-input"
                >
                  {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bathrooms</label>
                <select
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-bathrooms-input"
                >
                  {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Floors</label>
                <select
                  value={formData.floors}
                  onChange={(e) => setFormData({ ...formData, floors: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-floors-input"
                >
                  {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Estimated Construction Cost (R)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="e.g. 830000"
                  value={formData.estimated_construction_cost || 0}
                  onChange={(e) => setFormData({ ...formData, estimated_construction_cost: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-est-cost-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Building Type</label>
                <select
                  value={formData.plan_type}
                  onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                  className="w-full p-2 border border-border rounded"
                  data-testid="plan-type-input"
                >
                  {buildingTypes.length === 0
                    ? ['A', 'B', 'C', 'D', 'E'].map(t => <option key={t} value={t}>Type {t}</option>)
                    : buildingTypes.map(t => <option key={t.type_code} value={t.type_code}>{t.label}</option>)
                  }
                </select>
              </div>
            </div>
          </section>

          {/* Images */}
          <section>
            <h3 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Plan Images</h3>
            <p className="text-xs text-text_muted mb-3">Add a short name/label for each image (used for alt text & SEO metadata — not displayed to customers).</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
              {formData.images.map((img, i) => (
                <div key={i} className="border border-border overflow-hidden group" data-testid={`image-item-${i}`}>
                  <div className="relative aspect-square">
                    <img src={img} alt={`Plan ${i}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${i}`}
                      aria-label="Remove image"
                    >
                      <Trash size={14} />
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-accent text-white px-1.5 py-0.5 uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Primary</span>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Image name / label"
                    value={(formData.image_items?.[i]?.name) || ''}
                    onChange={(e) => setImageName(i, e.target.value)}
                    className="w-full px-2 py-1.5 border-t border-border text-xs focus:outline-none focus:bg-orange-50/50"
                    data-testid={`image-name-${i}`}
                  />
                </div>
              ))}
              <label className="aspect-square border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-accent hover:bg-gray-50 transition-colors" data-testid="upload-image-btn">
                <Plus size={24} className="text-text_muted mb-1" />
                <span className="text-xs text-text_muted">{imageUploading ? 'Uploading...' : 'Add image'}</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={imageUploading} className="hidden" />
              </label>
            </div>
          </section>

          {/* Drawing Sets */}
          <section>
            <h3 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Drawing Sets, Prices & Files</h3>
            <p className="text-xs text-text_muted mb-3">Toggle each drawing set on/off, set the PDF price, and upload the corresponding PDF and CAD files. The system automatically adds 35% for the CAD+PDF option to the Architectural Drawings price only.</p>
            <div className="space-y-3">
              {DRAWING_SETS_META.map(({ key, label, required }) => {
                const ds = formData.drawing_sets[key] || emptyDrawingSet;
                const isEnabled = ds.enabled || required;
                return (
                  <div key={key} className={`border p-3 ${isEnabled ? 'border-accent bg-orange-50/30' : 'border-border bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          disabled={required}
                          onChange={() => toggleDrawingSet(key)}
                          className="mr-2"
                          data-testid={`toggle-drawing-set-${key}`}
                        />
                        <span className="font-medium text-sm">{label}</span>
                        {required && <span className="text-accent text-xs ml-2">(Always enabled)</span>}
                      </label>
                      {isEnabled && (
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-text_muted">Price (R):</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={ds.price || 0}
                            onChange={(e) => setDrawingSetPrice(key, e.target.value)}
                            className="w-28 p-1.5 border border-border rounded text-sm"
                            data-testid={`price-drawing-set-${key}`}
                          />
                        </div>
                      )}
                    </div>
                    {isEnabled && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <FileUploader
                          label="PDF file"
                          accept=".pdf"
                          currentFileId={ds.pdf_file_id}
                          currentFilename={ds.pdf_filename}
                          onUploaded={(data) => setDrawingSetFile(key, 'pdf', data)}
                          onCleared={() => setDrawingSetFile(key, 'pdf', null)}
                          testId={`upload-pdf-${key}`}
                        />
                        <FileUploader
                          label="CAD file"
                          accept=".dwg,.dxf,.zip"
                          currentFileId={ds.cad_file_id}
                          currentFilename={ds.cad_filename}
                          onUploaded={(data) => setDrawingSetFile(key, 'cad', data)}
                          onCleared={() => setDrawingSetFile(key, 'cad', null)}
                          testId={`upload-cad-${key}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-border rounded hover:bg-gray-50"
              data-testid="cancel-form-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-accent text-white rounded font-medium hover:bg-opacity-90 disabled:opacity-50"
              data-testid="save-plan-btn"
            >
              {saving ? 'Saving...' : (existingPlan ? 'Update Plan' : 'Create Plan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanForm;
